/**
 * Maps raw Cognito error codes to calm, human copy.
 *
 * Sign-in failures for wrong password and unknown email use the same message
 * so the login form doesn't reveal which addresses have accounts.
 */
export function friendlyAuthError(err: unknown, fallback: string): string {
  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code: unknown }).code)
      : '';
  const message = err instanceof Error ? err.message : '';

  switch (code) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return "That email and password don't match. Check them and try again.";
    case 'UsernameExistsException':
      return 'An account with this email already exists. Try signing in instead.';
    case 'InvalidPasswordException':
      return 'That password is too simple — use at least 8 characters with a mix of letters and numbers.';
    case 'InvalidParameterException':
      return 'Something in the form doesn’t look right. Check your details and try again.';
    case 'CodeMismatchException':
      return "That code doesn't match. Double-check the code from your email and try again.";
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one and try again.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many attempts. Wait a few minutes, then try again.';
    case 'NetworkError':
      return "Couldn't reach the server. Check your internet connection and try again.";
  }

  // UserNotConfirmedException is handled by the login page (redirects to
  // verification), and anything unrecognized falls back to the raw message
  // when it exists — better a techy message than no information at all.
  return message || fallback;
}
